/**
 * Slice 44 — the Meta ad surfaces, drawn from one read: the whole, then a
 * panel per surface. The draft's page draws a campaign over its lifetime;
 * Analytics draws the ad account over a period.
 *
 * ## THE WHOLE COMES FIRST, BECAUSE REACH DOES NOT ADD UP
 *
 * "People reached" on the whole is Meta's own count, overlap removed. The
 * panels' reaches are each surface's, and a person who saw the ad on two of
 * them is in each — so they sum to more than the whole, and nothing here sums
 * them. Reach is drawn as a number, never a bar. Spend adds up, so it is the
 * one figure each panel measures against the whole with a bar.
 *
 * ## OTHER PLACEMENTS ARE DRAWN AS THEMSELVES
 *
 * Audience Network, Messenger and whatever Meta adds are placements, not
 * surfaces. Their spend is real, so they get a panel of their own — when the
 * read has them — rather than vanishing, or being folded into Facebook.
 *
 * ## FIGURES, CHIPS AND LABELS — NO SENTENCES
 *
 * A figure Meta could not measure is the "Not measured" chip, never a zero; a
 * figure this product cannot print in its currency is the currency's chip. The
 * tag above the figures says what they cover — "Lifetime" on the draft's page,
 * whose reading above already says when it was read; the account and the days
 * on Analytics — in the account read's tag style, and "Simulated" says the
 * stub answered. Slice 52 puts one more fact in that row when it applies:
 * "Not started from Wend", passed in as `aside`.
 *
 * ## A PLACEMENT META DID NOT MENTION GETS NO PANEL
 *
 * Absent from `byPlacement`, a null figure and a zero are three different
 * answers and are drawn three ways: no panel at all, the "Not measured" chip,
 * and `0`. That distinction was always here; slice 52 made it load-bearing —
 * a campaign somebody started in Ads Manager can be read before Meta has
 * anything to say about it — so it is asserted rather than assumed.
 */
import { ProportionBar, StateChip } from '@wend/ui';
import type { ReactNode } from 'react';

/* `@wend/shared` directly, never through the API client. */
import type { MetaAdsFigures, MetaAdsPlatformFigures, MetaAdsSurfaceFigures } from '@wend/shared';

import { formatCount } from '../../lib/format';
import { placementLabel } from '../../lib/surface-display';
import { Figure, formatMetaMoney } from '../integrations/MetaAdsAccountRead';

export interface MetaAdSurfacePanelsProps {
  readonly figures: MetaAdsSurfaceFigures;
  /** What the figures cover, printed in the tag above them — "Lifetime", or an account and its days. */
  readonly covers: ReactNode;
  /**
   * Whether the whole's spend is drawn. The draft's page says it already —
   * "Spent", in Meta's reading above — and one figure is not drawn twice.
   */
  readonly wholeSpend?: boolean;
  /**
   * Anything further about where the figures came from, drawn in the tag row
   * beside "Lifetime" and "Simulated" — slice 52's "Not started from Wend"
   * badge is its only caller. It belongs in that row because provenance is
   * what the row is: what these figures cover, and who produced them.
   */
  readonly aside?: ReactNode;
}

export function MetaAdSurfacePanels({ figures, covers, wholeSpend = true, aside }: MetaAdSurfacePanelsProps) {
  const money = (minor: number | null): ReactNode => {
    if (minor === null) return <StateChip state="not_measured" label="Not measured" />;
    return formatMetaMoney(minor, figures.currency) ?? <StateChip state="not_measured" label={figures.currency} />;
  };
  const count = (value: number | null): ReactNode =>
    value === null ? <StateChip state="not_measured" label="Not measured" /> : formatCount(value);

  return (
    <section data-role="meta-ad-surfaces" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <span data-role="meta-ad-surfaces-covers" className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
          {covers}
        </span>
        {figures.transport === 'stub' && <StateChip state="estimated" label="Simulated" />}
        {aside}
      </div>

      <dl data-role="meta-ad-surfaces-whole" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {wholeSpend && <Figure label="Spend" kind="money">{money(figures.figures.spend)}</Figure>}
        <Figure label="Impressions">{count(figures.figures.impressions)}</Figure>
        {/* The whole's reach — Meta's own, overlap removed. Never a sum of the panels'. */}
        <Figure label="People reached">{count(figures.figures.reach)}</Figure>
        <Figure label="Clicks">{count(figures.figures.clicks)}</Figure>
      </dl>

      <ul data-role="meta-ad-surface-panels" className="grid gap-3 md:grid-cols-2">
        {figures.byPlacement.map((row) => (
          <SurfacePanel
            key={row.placement}
            row={row}
            spendTotal={figures.figures.spend}
            money={money}
            count={count}
          />
        ))}
      </ul>
    </section>
  );
}

function SurfacePanel({
  row,
  spendTotal,
  money,
  count,
}: {
  row: MetaAdsPlatformFigures;
  spendTotal: number | null;
  money: (minor: number | null) => ReactNode;
  count: (value: number | null) => ReactNode;
}) {
  const figures: MetaAdsFigures = row.figures;
  return (
    <li
      data-role="meta-ad-surface-panel"
      data-placement={row.placement}
      className="flex flex-col gap-3 rounded-base border border-line p-4"
    >
      <span className="text-[0.88rem] text-text">{placementLabel(row.placement)}</span>
      {/* Spend is additive, so it is the one figure a bar may compare with the whole. */}
      {figures.spend !== null && spendTotal !== null && spendTotal > 0 && (
        <ProportionBar tone="neutral" value={figures.spend} total={spendTotal} />
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
        <PanelFigure label="Spend">{money(figures.spend)}</PanelFigure>
        <PanelFigure label="Impressions">{count(figures.impressions)}</PanelFigure>
        {/* A number, never a bar: a surface's reach does not add up to the whole's. */}
        <PanelFigure label="People reached">{count(figures.reach)}</PanelFigure>
        <PanelFigure label="Clicks">{count(figures.clicks)}</PanelFigure>
      </dl>
    </li>
  );
}

/**
 * A panel's figure: the account read's label style, a smaller figure. Slice 45's
 * comparison tiles use it too, so their labels sit where the panels' do.
 */
export function PanelFigure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-muted">{label}</dt>
      <dd className="mt-1 font-mono text-[0.92rem] tabular-nums text-text">{children}</dd>
    </div>
  );
}
